/**
 * @fileoverview WYSIWYG editor for the lesson free-text fields.
 *
 * Clicking Bold makes text bold — the instructor never sees or types a marker.
 * What gets STORED is still the same plain "lesson markdown" string every
 * other surface renders (`lib/richText.js`), so the email, PDF, portal and
 * mobile app are untouched by this and nothing already saved changes meaning.
 * The editor is a view over that string, not a new format.
 *
 * Built on `contenteditable` + `execCommand` rather than a rich-text library:
 * the whole supported set is bold / italic / underline / bulleted / numbered,
 * and pulling in ProseMirror or Lexical to express five commands would be a large
 * dependency for a document that must round-trip losslessly to plain text
 * anyway. `execCommand` is formally deprecated but universally implemented,
 * and every browser's quirks land in the serializer, which is the one place
 * we can test them.
 *
 * Three things this must get right, each with a test that fails without it:
 *   1. Round-trip fidelity — opening and closing the editor without typing
 *      must not rewrite a single character of stored text.
 *   2. Paste is plain text only — pasting from Word or a browser would
 *      otherwise inject arbitrary HTML into a document that has no sanitiser.
 *   3. The caret survives a re-render — the value is controlled, so naively
 *      re-seeding innerHTML on every keystroke would send the cursor home.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@/components/studio/primitives";
import { editorDomToRichText, richTextToEditorHtml } from "@/lib/richText";

const ALL_ACTIONS = {
  bold: { key: "bold", command: "bold", label: "Bold", icon: "bold", shortcut: "⌘B" },
  italic: { key: "italic", command: "italic", label: "Italic", icon: "italic", shortcut: "⌘I" },
  underline: { key: "underline", command: "underline", label: "Underline", icon: "underline", shortcut: "⌘U" },
  bullet: { key: "bullet", command: "insertUnorderedList", label: "Bulleted list", icon: "list" },
  ordered: { key: "ordered", command: "insertOrderedList", label: "Numbered list", icon: "listOrdered" },
};

/** The lesson editors' set. Underline is offered where a caller asks for it. */
export const LESSON_ACTIONS = ["bold", "italic", "bullet", "ordered"];

/** Keyboard shortcuts, by key, for the inline commands. */
const SHORTCUTS = { b: "bold", i: "italic", u: "underline" };

/**
 * @param {Object} props
 * @param {string} props.value - Stored lesson markdown.
 * @param {(next: string) => void} props.onChange - Receives lesson markdown.
 * @param {string} [props.placeholder]
 * @param {string} [props.className] - Typography for the editable surface.
 * @param {string} [props.ariaLabel]
 * @param {number} [props.minHeight]
 * @param {string} props.testId - Applied to the editable element.
 * @param {string[]} [props.actions] - Toolbar buttons, from bold, italic,
 *   underline, bullet and ordered. Defaults to the lesson editors' set.
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  className = "",
  ariaLabel,
  minHeight = 180,
  testId,
  actions: actionKeys = LESSON_ACTIONS,
}) {
  const ref = useRef(null);
  const actions = useMemo(() => actionKeys.map((k) => ALL_ACTIONS[k]).filter(Boolean), [actionKeys]);
  // The last markdown we ourselves emitted. Re-seeding the DOM only when the
  // incoming value differs from this is what keeps the caret alive: without
  // it every keystroke would rewrite innerHTML and send the cursor to the top.
  const lastEmitted = useRef(null);
  const [active, setActive] = useState({});
  const [isEmpty, setIsEmpty] = useState(!value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value === lastEmitted.current) return;
    el.innerHTML = richTextToEditorHtml(value);
    lastEmitted.current = value;
    setIsEmpty(!value);
  }, [value]);

  /** Reflect the caret's formatting on the toolbar (aria-pressed + styling). */
  const syncActive = useCallback(() => {
    if (typeof document.queryCommandState !== "function") return;
    const next = {};
    for (const action of actions) {
      try {
        next[action.key] = document.queryCommandState(action.command);
      } catch {
        next[action.key] = false;
      }
    }
    setActive(next);
  }, [actions]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const markdown = editorDomToRichText(el);
    lastEmitted.current = markdown;
    setIsEmpty(!markdown);
    onChange(markdown);
  }, [onChange]);

  const exec = useCallback(
    (command) => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      document.execCommand(command, false, null);
      emit();
      syncActive();
    },
    [emit, syncActive],
  );

  const handleKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const command = SHORTCUTS[e.key.toLowerCase()];
    if (!command) return;
    // Browsers already map ⌘B/⌘I/⌘U inside contenteditable, but handling them
    // here keeps the emitted value and the toolbar state in step with the DOM
    // rather than relying on the input event ordering. A shortcut for a
    // command this editor does not offer is refused: it would look applied
    // and then be gone the next time the text is opened.
    e.preventDefault();
    if (actions.some((a) => a.command === command)) exec(command);
  };

  /**
   * Paste as plain text. A document with no sanitiser must never accept
   * arbitrary markup, and pasting a styled block from Word or a web page is
   * exactly how that gets attempted.
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") ?? "";
    document.execCommand("insertText", false, text);
    emit();
  };

  return (
    <div>
      <div
        className="flex flex-wrap items-center gap-1"
        role="toolbar"
        aria-label="Text formatting"
        data-testid={`${testId}-toolbar`}
      >
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            aria-label={action.label}
            aria-pressed={!!active[action.key]}
            title={action.shortcut ? `${action.label} (${action.shortcut})` : action.label}
            data-testid={`${testId}-${action.key}`}
            // Keep focus in the editor: a mousedown that blurs it would drop
            // the selection the command is supposed to act on.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(action.command)}
            className={`flex h-7 w-7 items-center justify-center rounded-md border transition-colors ${
              active[action.key]
                ? "border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                : "border-transparent text-[var(--text-tertiary)] hover:border-[var(--studio-divider)] hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Icon name={action.icon} size={15} />
          </button>
        ))}
      </div>

      <div className="relative mt-2">
        {isEmpty && placeholder && (
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute left-3 top-2 select-none text-[var(--text-quaternary)] ${className}`}
          >
            {placeholder}
          </span>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel}
          data-testid={testId}
          onInput={emit}
          onBlur={emit}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onKeyUp={syncActive}
          onMouseUp={syncActive}
          onFocus={syncActive}
          style={{ minHeight }}
          className={`w-full rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3 py-2 text-[var(--text-primary)] focus:border-[var(--accent-yellow-border-strong)] focus:outline-none [&_li]:ml-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0 [&_ul]:list-disc [&_ul]:pl-5 ${className}`}
        />
      </div>
    </div>
  );
}
